import React, { useState } from 'react';
import {
  Download,
  FileText,
  Filter,
  Search,
  RefreshCw,
  ArrowRightLeft,
  GitBranch,
  Users,
  Activity,
} from 'lucide-react';
import { auditExportApi } from '../services/api';
import Pagination from '../components/Pagination';
import { formatDate } from '../utils/helpers';

// ── Status badge helper ────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    COMMITTED: 'badge-success',
    RECEIVED:  'badge-success',
    RESOLVED:  'badge-success',
    RESERVED:  'bg-blue-100 text-blue-800',
    PENDING:   'badge-info',
    ABORTED:   'badge-danger',
    FAILED:    'badge-danger',
    ERROR:     'badge-danger',
    NOT_FOUND: 'badge-warning',
    EXPIRED:   'badge-warning',
  };
  const cls = map[status] || 'bg-gray-100 text-gray-600';
  return <span className={`badge ${cls}`}>{status ?? '—'}</span>;
};

// ── Stat card ──────────────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="stat-card">
    <div className="flex items-center justify-between mb-3">
      <div className={`p-2 rounded-lg ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
    <p className="text-sm text-gray-600">{label}</p>
    <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
  </div>
);

// ── Main page ──────────────────────────────────────────────────────────────
const AuditExport = () => {
  const today = new Date().toISOString().split('T')[0];
  const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];

  const [filters, setFilters] = useState({
    from:        thirtyDaysAgo,
    to:          today,
    search:      '',
    reportTitle: '',
  });
  const [preview, setPreview]       = useState(null);
  const [loading, setLoading]       = useState(false);
  const [downloading, setDownloading] = useState(null); // 'csv' | 'pdf' | null
  const [error, setError]           = useState(null);
  const [activeTab, setActiveTab]   = useState('transfers');
  const [tabPages, setTabPages]     = useState({ transfers: 1, quotes: 1, partyLookups: 1, events: 1 });
  const PAGE_SIZE = 20;

  const tabPage  = (tab) => tabPages[tab] ?? 1;
  const paginate = (list, tab) => {
    const p = tabPage(tab);
    return list.slice((p - 1) * PAGE_SIZE, p * PAGE_SIZE);
  };
  const setTabPage = (tab, p) => setTabPages(prev => ({ ...prev, [tab]: p }));
  // Reset all tab pages when a new preview loads
  const resetPages = () => setTabPages({ transfers: 1, quotes: 1, partyLookups: 1, events: 1 });

  // ── Load preview ──────────────────────────────────────────────────────────
  const loadPreview = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await auditExportApi.preview(filters);
      setPreview(data);
      resetPages();
    } catch (err) {
      console.error('[AuditExport] preview error:', err);
      setError('Failed to load preview. Check your filters and try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Download ──────────────────────────────────────────────────────────────
  const handleDownload = async (format) => {
    try {
      setDownloading(format);
      const dateStr = new Date().toISOString().split('T')[0].replace(/-/g, '');
      if (format === 'csv') {
        await auditExportApi.downloadCsv(filters, `fspiop_audit_${dateStr}.csv`);
      } else {
        await auditExportApi.downloadPdf(filters, `fspiop_audit_${dateStr}.pdf`);
      }
    } catch (err) {
      console.error('[AuditExport] download error:', err);
      setError(`Failed to download ${format.toUpperCase()}. Please try again.`);
    } finally {
      setDownloading(null);
    }
  };

  const setFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Audit Export</h1>
          <p className="text-gray-600 mt-1">
            FSPIOP transaction compliance report — transfers, quotes, party lookups and events
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => handleDownload('csv')}
            disabled={!!downloading || loading}
            className="btn-secondary flex items-center space-x-2"
          >
            {downloading === 'csv'
              ? <RefreshCw className="w-4 h-4 animate-spin" />
              : <Download className="w-4 h-4" />
            }
            <span>CSV</span>
          </button>
          <button
            onClick={() => handleDownload('pdf')}
            disabled={!!downloading || loading}
            className="btn-primary flex items-center space-x-2"
          >
            {downloading === 'pdf'
              ? <RefreshCw className="w-4 h-4 animate-spin" />
              : <FileText className="w-4 h-4" />
            }
            <span>PDF</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-header flex items-center space-x-2">
          <Filter className="w-5 h-5 text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-900">Report Filters</h2>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
              <input
                type="date"
                className="input"
                value={filters.from}
                onChange={(e) => setFilter('from', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
              <input
                type="date"
                className="input"
                value={filters.to}
                onChange={(e) => setFilter('to', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  className="input pl-9"
                  placeholder="Transfer ID, FSP, party…"
                  value={filters.search}
                  onChange={(e) => setFilter('search', e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Report Title</label>
              <input
                type="text"
                className="input"
                placeholder="FSPIOP Transaction Audit Report"
                value={filters.reportTitle}
                onChange={(e) => setFilter('reportTitle', e.target.value)}
              />
            </div>
          </div>
          <div className="mt-4 flex items-center space-x-3">
            <button
              onClick={loadPreview}
              disabled={loading}
              className="btn-primary flex items-center space-x-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Load Preview</span>
            </button>
            <button
              onClick={() => {
                setFilters({ from: thirtyDaysAgo, to: today, search: '', reportTitle: '' });
                setPreview(null);
              }}
              className="btn-secondary"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-danger-50 border border-danger-200 rounded-lg p-4 text-danger-700">
          {error}
        </div>
      )}

      {/* Preview — counts */}
      {preview && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              icon={ArrowRightLeft}
              label="Transfers"
              value={preview.transfers?.length ?? 0}
              color="bg-primary-100 text-primary-600"
            />
            <StatCard
              icon={GitBranch}
              label="Quotes"
              value={preview.quotes?.length ?? 0}
              color="bg-success-100 text-success-600"
            />
            <StatCard
              icon={Users}
              label="Party Lookups"
              value={preview.partyLookups?.length ?? 0}
              color="bg-blue-100 text-blue-600"
            />
            <StatCard
              icon={Activity}
              label="Events"
              value={preview.events?.length ?? 0}
              color="bg-purple-100 text-purple-600"
            />
          </div>

          {/* Meta */}
          <div className="card">
            <div className="card-body">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Report Title</p>
                  <p className="font-medium text-gray-900">{preview.reportTitle}</p>
                </div>
                <div>
                  <p className="text-gray-500">Generated</p>
                  <p className="font-medium text-gray-900">{preview.generatedAt} UTC</p>
                </div>
                <div>
                  <p className="text-gray-500">Period</p>
                  <p className="font-medium text-gray-900">{preview.fromDate} → {preview.toDate}</p>
                </div>
                <div>
                  <p className="text-gray-500">Total Records</p>
                  <p className="font-medium text-gray-900">
                    {(preview.transfers?.length ?? 0)
                      + (preview.quotes?.length ?? 0)
                      + (preview.partyLookups?.length ?? 0)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabbed preview table */}
          <div className="card">
            <div className="card-header">
              <div className="flex space-x-1 border-b border-gray-200">
                {[
                  { key: 'transfers',    label: `Transfers (${preview.transfers?.length ?? 0})` },
                  { key: 'quotes',       label: `Quotes (${preview.quotes?.length ?? 0})` },
                  { key: 'partyLookups', label: `Party Lookups (${preview.partyLookups?.length ?? 0})` },
                  { key: 'events',       label: `Events (${preview.events?.length ?? 0})` },
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => { setActiveTab(tab.key); setTabPage(tab.key, 1); }}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === tab.key
                        ? 'border-primary-600 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="card-body p-0 overflow-x-auto">
              {/* Transfers tab */}
              {activeTab === 'transfers' && (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {['ID', 'Transfer ID', 'Payer FSP', 'Payee FSP', 'Amount', 'Status', 'Created'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {paginate(preview.transfers ?? [], 'transfers').map(t => (
                      <tr key={t.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-mono text-gray-900">#{t.id}</td>
                        <td className="px-4 py-3 text-sm font-mono text-gray-900">{t.transferId?.substring(0, 16)}…</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{t.payerFsp}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{t.payeeFsp}</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {t.amount} <span className="text-gray-400 text-xs">{t.currency}</span>
                        </td>
                        <td className="px-4 py-3"><StatusBadge status={t.status} /></td>
                        <td className="px-4 py-3 text-sm text-gray-500">{formatDate(t.createdAt, 'PPp')}</td>
                      </tr>
                    ))}
                    {(preview.transfers ?? []).length === 0 && (
                      <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No transfers in this period</td></tr>
                    )}
                  </tbody>
                </table>
              )}

              {/* Quotes tab */}
              {activeTab === 'quotes' && (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {['ID', 'Quote ID', 'Payer FSP', 'Payee FSP', 'Amount', 'Status', 'Created'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {paginate(preview.quotes ?? [], 'quotes').map(q => (
                      <tr key={q.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-mono text-gray-900">#{q.id}</td>
                        <td className="px-4 py-3 text-sm font-mono text-gray-900">{q.quoteId?.substring(0, 16)}…</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{q.payerFsp}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{q.payeeFsp || '—'}</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {q.amount} <span className="text-gray-400 text-xs">{q.currency}</span>
                        </td>
                        <td className="px-4 py-3"><StatusBadge status={q.status} /></td>
                        <td className="px-4 py-3 text-sm text-gray-500">{formatDate(q.createdAt, 'PPp')}</td>
                      </tr>
                    ))}
                    {(preview.quotes ?? []).length === 0 && (
                      <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No quotes in this period</td></tr>
                    )}
                  </tbody>
                </table>
              )}

              {/* Party Lookups tab */}
              {activeTab === 'partyLookups' && (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {['ID', 'Party Type', 'Party ID', 'Resolved FSP', 'Resolved Name', 'Status', 'Created'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {paginate(preview.partyLookups ?? [], 'partyLookups').map(p => (
                      <tr key={p.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-mono text-gray-900">#{p.id}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{p.partyType}</td>
                        <td className="px-4 py-3 text-sm font-mono text-gray-900">{p.partyId}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{p.resolvedFspId || '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{p.resolvedPartyName || '—'}</td>
                        <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                        <td className="px-4 py-3 text-sm text-gray-500">{formatDate(p.createdAt, 'PPp')}</td>
                      </tr>
                    ))}
                    {(preview.partyLookups ?? []).length === 0 && (
                      <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No party lookups in this period</td></tr>
                    )}
                  </tbody>
                </table>
              )}

              {/* Events tab */}
              {activeTab === 'events' && (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {['Time', 'Level', 'Category', 'Message', 'Actor'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {paginate(preview.events ?? [], 'events').map(e => (
                      <tr key={e.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{formatDate(e.eventTime, 'PPp')}</td>
                        <td className="px-4 py-3">
                          <span className={`badge text-xs ${
                            e.level === 'ERROR' ? 'badge-danger'
                            : e.level === 'WARN' ? 'badge-warning'
                            : 'badge-info'
                          }`}>{e.level}</span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600">{e.category}</td>
                        <td className="px-4 py-3 text-xs text-gray-800 max-w-xs truncate">{e.message}</td>
                        <td className="px-4 py-3 text-xs font-mono text-gray-600">{e.actor || '—'}</td>
                      </tr>
                    ))}
                    {(preview.events ?? []).length === 0 && (
                      <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">No events in this period</td></tr>
                    )}
                  </tbody>
                </table>
              )}

              {/* Per-tab pagination */}
              {activeTab === 'transfers' && (preview.transfers?.length ?? 0) > PAGE_SIZE && (
                <Pagination
                  currentPage={tabPage('transfers')}
                  totalPages={Math.ceil((preview.transfers?.length ?? 0) / PAGE_SIZE)}
                  totalItems={preview.transfers?.length ?? 0}
                  pageSize={PAGE_SIZE}
                  onPageChange={(p) => setTabPage('transfers', p)}
                  pageSizeOptions={[]}
                  loading={false}
                />
              )}
              {activeTab === 'quotes' && (preview.quotes?.length ?? 0) > PAGE_SIZE && (
                <Pagination
                  currentPage={tabPage('quotes')}
                  totalPages={Math.ceil((preview.quotes?.length ?? 0) / PAGE_SIZE)}
                  totalItems={preview.quotes?.length ?? 0}
                  pageSize={PAGE_SIZE}
                  onPageChange={(p) => setTabPage('quotes', p)}
                  pageSizeOptions={[]}
                  loading={false}
                />
              )}
              {activeTab === 'partyLookups' && (preview.partyLookups?.length ?? 0) > PAGE_SIZE && (
                <Pagination
                  currentPage={tabPage('partyLookups')}
                  totalPages={Math.ceil((preview.partyLookups?.length ?? 0) / PAGE_SIZE)}
                  totalItems={preview.partyLookups?.length ?? 0}
                  pageSize={PAGE_SIZE}
                  onPageChange={(p) => setTabPage('partyLookups', p)}
                  pageSizeOptions={[]}
                  loading={false}
                />
              )}
              {activeTab === 'events' && (preview.events?.length ?? 0) > PAGE_SIZE && (
                <Pagination
                  currentPage={tabPage('events')}
                  totalPages={Math.ceil((preview.events?.length ?? 0) / PAGE_SIZE)}
                  totalItems={preview.events?.length ?? 0}
                  pageSize={PAGE_SIZE}
                  onPageChange={(p) => setTabPage('events', p)}
                  pageSizeOptions={[]}
                  loading={false}
                />
              )}
            </div>
          </div>

          {/* Download actions */}
          <div className="card">
            <div className="card-body">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">Download Full Report</p>
                  <p className="text-sm text-gray-500">
                    All {(preview.transfers?.length ?? 0)
                      + (preview.quotes?.length ?? 0)
                      + (preview.partyLookups?.length ?? 0)} transaction records
                    + {preview.events?.length ?? 0} events
                  </p>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => handleDownload('csv')}
                    disabled={!!downloading}
                    className="btn-secondary flex items-center space-x-2"
                  >
                    {downloading === 'csv'
                      ? <RefreshCw className="w-4 h-4 animate-spin" />
                      : <Download className="w-4 h-4" />
                    }
                    <span>Download CSV</span>
                  </button>
                  <button
                    onClick={() => handleDownload('pdf')}
                    disabled={!!downloading}
                    className="btn-primary flex items-center space-x-2"
                  >
                    {downloading === 'pdf'
                      ? <RefreshCw className="w-4 h-4 animate-spin" />
                      : <FileText className="w-4 h-4" />
                    }
                    <span>Download PDF</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Empty state (before first preview load) */}
      {!preview && !loading && (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-900 font-medium mb-2">No preview loaded</p>
            <p className="text-gray-600 text-sm">
              Set your date range and click <strong>Load Preview</strong> to see report counts,
              then download as CSV or PDF.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditExport;
